import { useCallback, useEffect, useMemo, useState } from 'react';
import type { OrderUpdateAPI, OrderWaybillBayanStatus } from '@hero/api';
import type {
  ItemPacking,
  ItemType,
  Order,
  VehicleTypes,
  WaybillBlocker,
  Weight,
} from '@hero/schema';
import { resolveLoadPackingAndQuantityFromVehicles } from '@hero/core';
import { isBayanEstablishmentNumber, toBayanEstablishmentNumber } from '@hero/apps';
import {
  useAppIssueWaybillBayanApi,
  useAppLogistiInstallApi,
  useAppWaybillBayanStatusApi,
  useOrderUpdateApi,
  useOrgProfileApi,
  useOrgProfileUpdateApi,
} from '@hero/api-hooks';
import { buildUpdateOrderPricePayload } from '../order/order-draft/buildUpdateOrderPricePayload';
import { useViewerLeg } from '../order/useViewerLeg';

/**
 * Every value the government-waybill form collects itself.
 *
 * Deliberately does NOT cover the driver's ID / phone or the truck's plate.
 * Those live on the Driver and Vehicle records, which have their own edit forms
 * with their own validation, and duplicating a subset of those fields here
 * produced two places to fix the same truck. The form now sends the user to
 * those forms and only takes the values that belong to THIS order (its load,
 * its fare) or to the Org.
 */
export interface GovernmentWaybillDraft {
  itemDescription: string;
  itemType: ItemType | undefined;
  packing: ItemPacking | undefined;
  quantity: number;
  /** Tons, matching the load card and `OrderLoadEditDrawer`. */
  weight: number | undefined;
  itemCost: number | undefined;
  loadPrice: number | undefined;
  crNumber: string;
  /**
   * Bayan cargo type for this order, when Hero's item type can't pick one.
   *
   * Sent as an override on issue rather than stored on the order: it is a
   * Bayan-specific classification, and the load itself is already described
   * by `itemType` in Hero's own vocabulary.
   */
  goodTypeId: number | undefined;
}

/**
 * Seed the load fields from the order, falling back to what the accepted
 * vehicle types imply.
 *
 * `resolveLoadPackingAndQuantityFromVehicles` is the same helper the publish
 * flow and client order-settings use, so a truck order opens on 24 pallets and
 * a lorry on 8 — the number the dispatcher would have typed. Asking someone to
 * click a counter up from 1 to 24 is a worse default than being occasionally
 * wrong about a full truckload.
 */
/** Hero weight → tons for the form (Bayan mapper accepts ton on save). */
const weightToTons = (weight: Weight | undefined): number | undefined => {
  if (!weight?.value || weight.value <= 0) return undefined;
  if (weight.type === 'ton') return weight.value;
  if (weight.type === 'gram') return weight.value / 1_000_000;
  return weight.value / 1000;
};

const initialDraft = (order: Order): GovernmentWaybillDraft => {
  const item = order.load?.items?.[0];
  const fromVehicles = resolveLoadPackingAndQuantityFromVehicles(
    (order.load?.vehicles ?? []) as VehicleTypes[],
  );
  const weight =
    weightToTons(item?.weights) ?? weightToTons(order.load?.totalItemsWeight);

  return {
    itemDescription: item?.name ?? '',
    itemType: item?.type as ItemType | undefined,
    packing: (item?.packing as ItemPacking | undefined) ?? fromVehicles.packing,
    quantity: item?.quantity ?? fromVehicles.quantity,
    weight,
    itemCost: item?.cost,
    loadPrice: undefined,
    crNumber: '',
    goodTypeId: undefined,
  };
};

/** Which of this form's own inputs the order still needs. */
export interface GovernmentWaybillRequirements {
  load: boolean;
  weight: boolean;
  itemCost: boolean;
  loadPrice: boolean;
  crNumber: boolean;
  /** Bayan needs a specific cargo type Hero's item type doesn't pin down. */
  goodType: boolean;
  /** The Bayan branch the cargo type must come from, when the blocker named one. */
  goodTypeBranchId?: number;
}

/**
 * Blocker codes this form takes as an input of its own.
 *
 * Anything NOT in here and not owned by the Driver or Vehicle record is
 * reported as-is instead of being silently dropped — so a blocker code added
 * to the backend shows up in the form on day one rather than becoming an
 * invisible reason the Issue button stays disabled.
 */
const INLINE_ERROR_IDS = new Set([
  'bayan_waybill_missing_items',
  'bayan_waybill_missing_item_weight',
  'bayan_waybill_missing_item_price',
  'bayan_waybill_missing_fare',
  'bayan_waybill_missing_carrier_moi',
  'bayan_waybill_invalid_carrier_moi',
  'bayan_waybill_good_type_required',
  'bayan_waybill_good_type_not_a_leaf',
]);

const CARRIER_MOI_ERROR_IDS = new Set([
  'bayan_waybill_missing_carrier_moi',
  'bayan_waybill_invalid_carrier_moi',
]);

/** Canonical `bayan_waybill_*` id for comparisons — handles legacy short codes. */
const toBayanWaybillErrorId = (code: string): string => {
  if (code.startsWith('bayan_waybill_')) return code;
  if (code.startsWith('apps_waybill_')) return `bayan_waybill_${code.slice('apps_waybill_'.length)}`;
  if (!code.includes('_')) return `bayan_waybill_${code}`;
  return code;
};

export interface UseGovernmentWaybillManagerArgs {
  order: Order;
  /**
   * Gates the status read so a closed surface doesn't poll.
   *
   * Web passes the drawer's open state. Mobile renders this as a screen, where
   * mounted means open, so it passes `true`.
   */
  isOpen: boolean;
  /**
   * Called with the state Bayan came back with, issued or not.
   *
   * `issue()` never throws — a refusal is a 200 carrying the reason — so the
   * caller has to read the result to know what happened.
   */
  onSettled?: (result: OrderWaybillBayanStatus) => void;
}

/**
 * Orchestration behind the one-screen government-waybill form.
 *
 * Platform-free on purpose: web mounts it from a drawer, carrier-app from a
 * stack screen, and neither owns the rules about what Bayan will accept.
 *
 * ## Why blockers are grouped by `entity` rather than by code
 *
 * Every blocker the backend emits is already tagged with the record that owns
 * it (`driver`, `vehicle`, `order`, `org`, …). Grouping on that tag rather than
 * on a hand-maintained list of codes means the driver card lists whatever the
 * backend currently considers wrong with the driver, including codes added
 * after this file was written. The alternative — a `DRIVER_CODES` set here —
 * silently omits new codes, and the symptom is a disabled Issue button with no
 * stated reason.
 *
 * ## Why saving is folded into issuing
 *
 * Two buttons (Save, then Issue) means a state where the user has saved but
 * not issued, and a blocker list that has to be re-fetched in between to stay
 * honest. Pressing Issue writes every dirty value first and then issues in the
 * same act, so there is no intermediate state to keep fresh — and the issue
 * response carries the authoritative new state.
 */
export const useGovernmentWaybillManager = ({
  order,
  isOpen,
  onSettled,
}: UseGovernmentWaybillManagerArgs) => {
  const orderId = String(order._id);

  const [draft, setDraft] = useState<GovernmentWaybillDraft>(() => initialDraft(order));
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);

  const status = useAppWaybillBayanStatusApi(orderId, { enabled: isOpen });
  const { data: orgProfile } = useOrgProfileApi();
  const leg = useViewerLeg(order);

  const updateOrder = useOrderUpdateApi({});
  const updateOrgProfile = useOrgProfileUpdateApi({});
  const installLogisti = useAppLogistiInstallApi({
    invalidateQueryKeys: [['app'], ['order'], ['orders']],
  });

  const issueWaybill = useAppIssueWaybillBayanApi({
    // The order document carries `waybillBayan`, so every surface reading the
    // order — drawer, actions menu, table row — has to re-read it too.
    invalidateQueryKeys: [['order'], ['orders']],
    onSuccess: result => onSettled?.(result),
  });

  const waybill = status.data;
  const blockers = waybill?.blockers ?? [];
  const codes = useMemo(
    () => new Set(blockers.map(blocker => toBayanWaybillErrorId(blocker.code))),
    [blockers],
  );

  const isIssued = waybill?.status === 'issued' || waybill?.status === 'closed';

  // Reset when the surface closes so a second open never shows the last
  // session's half-filled form against a different order's blockers.
  useEffect(() => {
    if (isOpen) return;
    setDraft(initialDraft(order));
    setSaveError(undefined);
  }, [isOpen, order]);

  const requirements = useMemo<GovernmentWaybillRequirements>(
    () => ({
      load: codes.has('bayan_waybill_missing_items'),
      weight: codes.has('bayan_waybill_missing_item_weight'),
      itemCost: codes.has('bayan_waybill_missing_item_price'),
      loadPrice: codes.has('bayan_waybill_missing_fare'),
      crNumber:
        codes.has('bayan_waybill_missing_carrier_moi') ||
        codes.has('bayan_waybill_invalid_carrier_moi'),
      goodType:
        codes.has('bayan_waybill_good_type_required') ||
        codes.has('bayan_waybill_good_type_not_a_leaf'),
      goodTypeBranchId: blockers.find(blocker => blocker.goodTypeBranchId)?.goodTypeBranchId,
    }),
    [codes, blockers],
  );

  const driverBlockers = useMemo<WaybillBlocker[]>(
    () =>
      blockers.filter(
        blocker =>
          blocker.entity === 'driver' &&
          !CARRIER_MOI_ERROR_IDS.has(toBayanWaybillErrorId(blocker.code)),
      ),
    [blockers],
  );

  /** What is wrong with the assigned truck — fixed in the vehicle's own form. */
  const vehicleBlockers = useMemo<WaybillBlocker[]>(
    () => blockers.filter(blocker => blocker.entity === 'vehicle'),
    [blockers],
  );

  /**
   * Blockers no surface here can clear.
   *
   * Computed as a remainder rather than an allow-list, so an unrecognised
   * blocker is shown to the user instead of quietly gating the button.
   */
  const unfixableBlockers = useMemo<WaybillBlocker[]>(
    () =>
      blockers.filter(
        blocker =>
          blocker.entity !== 'driver' &&
          blocker.entity !== 'vehicle' &&
          !INLINE_ERROR_IDS.has(toBayanWaybillErrorId(blocker.code)),
      ),
    [blockers],
  );

  // Seed the fields that edit an EXISTING value rather than fill an empty one:
  // the CR number is an Org-level value the user may simply be confirming, and
  // the fare is usually already priced.
  useEffect(() => {
    if (!isOpen) return;
    setDraft(prev => ({
      ...prev,
      crNumber: prev.crNumber || (orgProfile?.registrationId ?? ''),
      loadPrice: prev.loadPrice ?? leg?.amount?.itemsOriginalSubtotal ?? undefined,
    }));
  }, [isOpen, orgProfile?.registrationId, leg?.amount?.itemsOriginalSubtotal]);

  const setField = useCallback(
    <K extends keyof GovernmentWaybillDraft>(key: K, value: GovernmentWaybillDraft[K]) =>
      setDraft(prev => ({ ...prev, [key]: value })),
    [],
  );

  /**
   * Everything this form still needs before Bayan will accept the order.
   *
   * Mirrors {@link requirements} one-for-one — a required input that is still
   * empty keeps the button disabled, so the user never spends a round-trip to
   * be told about a field that was visibly blank.
   */
  const missingFields = useMemo<Array<keyof GovernmentWaybillRequirements>>(() => {
    const missing: Array<keyof GovernmentWaybillRequirements> = [];
    const item = order.load?.items?.[0];
    const hasWeightOnOrder =
      weightToTons(item?.weights) != null || weightToTons(order.load?.totalItemsWeight) != null;

    if (!draft.itemType || !draft.itemDescription.trim()) missing.push('load');
    if ((requirements.weight || !hasWeightOnOrder) && !draft.weight) missing.push('weight');
    if (requirements.itemCost && !draft.itemCost) missing.push('itemCost');
    if (requirements.loadPrice && !draft.loadPrice) missing.push('loadPrice');
    if (requirements.crNumber && !isBayanEstablishmentNumber(draft.crNumber)) {
      missing.push('crNumber');
    }
    if (requirements.goodType && !draft.goodTypeId) missing.push('goodType');
    return missing;
  }, [requirements, draft, order]);

  const canIssue =
    !isIssued &&
    !isSaving &&
    !issueWaybill.isPending &&
    driverBlockers.length === 0 &&
    vehicleBlockers.length === 0 &&
    unfixableBlockers.length === 0 &&
    missingFields.length === 0;

  /**
   * Write every dirty value to the record that owns it.
   *
   * Sequential rather than parallel so a failure names the step that failed:
   * "couldn't save the load" is actionable, a rejected `Promise.all` is not.
   */
  const persistDraft = useCallback(async () => {
    const existingItem = order.load?.items?.[0];
    const items = [
      {
        ...(existingItem?._id ? { _id: existingItem._id } : {}),
        name: draft.itemDescription.trim() || existingItem?.name,
        type: draft.itemType ?? existingItem?.type,
        packing: draft.packing ?? existingItem?.packing,
        quantity: draft.quantity > 0 ? draft.quantity : (existingItem?.quantity ?? 1),
        weights:
          draft.weight && draft.weight > 0
            ? { value: draft.weight, type: 'ton' as const }
            : existingItem?.weights,
        cost: draft.itemCost ?? existingItem?.cost,
      },
    ] as OrderUpdateAPI['req']['items'];
    await updateOrder.mutateAsync({ _id: order._id, items });

    if (requirements.loadPrice && draft.loadPrice && leg?.contractId) {
      await updateOrder.mutateAsync(
        buildUpdateOrderPricePayload({
          orderId: order._id,
          contractId: leg.contractId,
          loadPrice: draft.loadPrice,
          isTaxable: (leg.amount?.tax?.applied?.length ?? 0) > 0,
          fees: (leg.amount?.fees?.applied ?? []).map(fee => ({
            type: fee.type,
            amount: fee.amount,
          })),
        }),
      );
    }

    const unifiedNumber = toBayanEstablishmentNumber(draft.crNumber);
    if (requirements.crNumber && unifiedNumber) {
      const crGoesToDriver = blockers.some(
        blocker =>
          CARRIER_MOI_ERROR_IDS.has(toBayanWaybillErrorId(blocker.code)) &&
          blocker.entity === 'driver',
      );
      if (crGoesToDriver) {
        await installLogisti.mutateAsync({
          filingMode: 'individual',
          crNumber: unifiedNumber,
        });
      } else if (unifiedNumber !== (orgProfile?.registrationId ?? '')) {
        await updateOrgProfile.mutateAsync({ legal: { registrationId: unifiedNumber } });
      }
    }
  }, [
    order,
    draft,
    requirements,
    leg,
    orgProfile?.registrationId,
    updateOrder,
    updateOrgProfile,
    installLogisti,
    blockers,
  ]);

  /** Save whatever the user filled in, then issue. */
  const submit = useCallback(async () => {
    setSaveError(undefined);
    setIsSaving(true);
    try {
      await persistDraft();
    } catch (caught) {
      setSaveError(caught instanceof Error ? caught.message : String(caught));
      setIsSaving(false);
      return;
    }
    setIsSaving(false);
    issueWaybill.mutate({ orderId, goodTypeId: draft.goodTypeId });
  }, [persistDraft, issueWaybill, orderId, draft.goodTypeId]);

  return {
    /** Live Bayan state for this order. */
    waybill,
    isLoading: status.isPending,
    isIssued,
    blockers,
    driverBlockers,
    vehicleBlockers,
    unfixableBlockers,
    /**
     * Re-read the blockers.
     *
     * The driver and vehicle forms write straight to their own records, so the
     * only way this form learns their blockers are gone is to ask again.
     */
    refetchStatus: status.refetch,

    draft,
    setField,
    requirements,
    missingFields,
    /** Currency of the viewer's leg, for the money inputs. */
    currency: leg?.amount?.currency,

    submit,
    canIssue,
    /** True from the first write until the issue call settles. */
    isSubmitting: isSaving || issueWaybill.isPending,
    isSaving,
    isIssuing: issueWaybill.isPending,
    error: saveError ?? issueWaybill.error?.message ?? waybill?.lastError,
  };
};
